import gql from 'graphql-tag'
import { getProvider } from '@/apollo'
import { splitError } from '@/helper/splitError'
import { beSpecialErrorCode } from '@/mixins/beErrors'
import mutationForgotPassword from './gql/mutationResetPassword.js'
import mutationRegistration from './gql/mutationResetPasswordInitial.js'

const ForgotPasswordModule = {
  namespaced: true,
  state: {
    resetPasswordParams: {
      msisdn: '',
      channel: null,
      view: 'input',
      validityPeriod: null,
      eligibleTokenChannels: []
    }
  },
  actions: {
    /*
    * Query, welche Channels (email,sms) dem User zur Verfügung stehen.
    */
    async resetPasswordChannels ({ commit, state, rootGetters }, { msisdn }) {
      const apolloClient = getProvider().defaultClient
      state.resetPasswordParams.msisdn = msisdn
      commit('clearMessages', {}, { root: true })
      return apolloClient.query({
        query: gql`query forgotPassword ($brand: String!, $msisdn: String!){
          resetPasswordChannels (brand: $brand, msisdn: $msisdn){
            eligibleTokenChannels,
            success
          },
        }`,
        variables: {
          msisdn: state.resetPasswordParams.msisdn,
          brand: rootGetters.brand
        }
      }).then((response) => {
        if (response.data.resetPasswordChannels.success) {
          state.resetPasswordParams.eligibleTokenChannels = response.data.resetPasswordChannels.eligibleTokenChannels
          if (!state.resetPasswordParams.eligibleTokenChannels.includes('sms')) {
            state.resetPasswordParams.channel = 'email'
          } else if (!state.resetPasswordParams.eligibleTokenChannels.includes('email')) {
            state.resetPasswordParams.channel = 'sms'
          }
          state.resetPasswordParams.view = 'channel'
          commit('Transaction/removeTransactionId', 'forgotPassword', { root: true })
        }
      }).catch((error) => {
        /* Sollten keine Channels möglich sein, wird von Magma ein Fehlercode zurückgegeben.
           Dieser Fehlercode 1118 wird individuell behandelt und auf eine neue Seite weitergeleitet.
           Alle anderen Errors werden in der Errorbox angezeigt. */
        if (beSpecialErrorCode(error, 1118)) {
          state.resetPasswordParams.view = 'error'
          state.resetPasswordParams.channel = null
          state.resetPasswordParams.eligibleTokenChannels = []
        } else {
          splitError(error).forEach(e => commit('addError', e, { root: true }))
        }
      })
    },
    /*
    * Mutation, Passwortreset anstoßen
    */
    async performResetChannel ({ commit, state, rootGetters }, { msisdn, channel, isRegistration }) {
      const apolloClient = getProvider().defaultClient
      state.resetPasswordParams.channel = channel
      state.resetPasswordParams.msisdn = msisdn
      commit('clearMessages', {}, { root: true })
      const mutation = isRegistration ? mutationRegistration : mutationForgotPassword
      return apolloClient.mutate({
        mutation: mutation,
        variables: {
          input: {
            msisdn: state.resetPasswordParams.msisdn,
            channel: state.resetPasswordParams.channel,
            brand: rootGetters.brand
          }
        }
      }).then((response) => {
        const result = isRegistration ? response.data.resetPasswordInitial : response.data.resetPassword
        if (result.validityPeriod !== null) {
          commit('resetPerformedSuccessfullyChannel', {
            channel: channel,
            validityPeriod: result.validityPeriod
          })
          commit('Transaction/removeTransactionId', 'forgotPassword', { root: true })
        }
      }).catch((error) => {
        splitError(error).forEach(e => commit('addError', e, { root: true }))
      })
    }
  },
  mutations: {
    captureMsisdn (state, msisdn) {
      state.resetPasswordParams.msisdn = msisdn
      state.resetPasswordParams.view = 'msisdn'
    },
    resetPerformedSuccessfullyChannel (state, { channel, validityPeriod }) {
      state.resetPasswordParams.channel = channel
      state.resetPasswordParams.validityPeriod = validityPeriod
      if (state.resetPasswordParams.view !== 'input' && state.resetPasswordParams.view !== 'channel') {
        return
      }
      if (channel === 'email') {
        state.resetPasswordParams.view = 'succeeded'
      } else if (channel === 'sms') {
        state.resetPasswordParams.view = 'token'
      }
    },
    restartProcess (state) {
      this.dispatch('forgotPassword/performResetChannel', {
        msisdn: state.resetPasswordParams.msisdn,
        channel: state.resetPasswordParams.channel
      }, { root: true })
    },
    captureToken (state, token, nextStep = 'password') {
      if (token) {
        this.commit('resetPassword/setToken', token, { root: true })
        state.resetPasswordParams.view = nextStep
      } else {
        state.resetPasswordParams.view = 'token'
      }
    },
    setView (state, viewState) {
      state.resetPasswordParams.view = viewState
    }
  },
  getters: {
    msisdn (state) {
      return state.resetPasswordParams.msisdn
    },
    validityPeriod (state) {
      return state.resetPasswordParams.validityPeriod
    },
    channel (state) {
      return state.resetPasswordParams.channel
    },
    view: state => {
      return state.resetPasswordParams.view
    },
    eligibleTokenChannels: state => {
      return state.resetPasswordParams.eligibleTokenChannels
    }
  }
}

export {
  ForgotPasswordModule
}
